import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { fetchBuyerOverview, fetchBuyerOrders, fetchBuyerRfqs } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, formatMoney } from '../../components/marketplace/cartUtils';

const BuyerOverviewPage: React.FC = () => {
  const [stats, setStats] = useState<any>({});
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [openRfqs, setOpenRfqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, ordersRes, rfqsRes] = await Promise.allSettled([
        fetchBuyerOverview().catch(() => null),
        fetchBuyerOrders({ limit: 5 }).catch(() => ({ orders: [] })),
        fetchBuyerRfqs({ status: 'published', limit: 5 }).catch(() => ({ items: [] })),
      ]);

      if (overviewRes.status === 'fulfilled' && overviewRes.value) {
        setStats(overviewRes.value.stats || overviewRes.value);
      }
      if (ordersRes.status === 'fulfilled') {
        setRecentOrders(ordersRes.value.orders || ordersRes.value.items || []);
      }
      if (rfqsRes.status === 'fulfilled') {
        setOpenRfqs(rfqsRes.value.items || []);
      }
    } catch (err) {
      console.error('Overview load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const metrics = [
    { label: 'Open Orders', value: stats.orders?.confirmed ?? '-', color: 'bg-blue-100 text-blue-700', href: '/account/orders' },
    { label: 'Awaiting Confirmation', value: stats.orders?.awaiting ?? '-', color: 'bg-amber-100 text-amber-700', href: '/account/orders' },
    { label: 'Open RFQs', value: stats.rfqs?.open ?? '-', color: 'bg-purple-100 text-purple-700', href: '/account/rfqs' },
    { label: 'Pending Quotations', value: stats.quotations?.pending ?? '-', color: 'bg-emerald-100 text-emerald-700', href: '/account/quotations' },
    { label: 'Active Deliveries', value: stats.shipments?.in_transit ?? '-', color: 'bg-indigo-100 text-indigo-700', href: '/account/deliveries' },
    { label: 'Outstanding Invoices', value: stats.invoices?.pending ?? '-', color: 'bg-rose-100 text-rose-700', href: '/account/invoices' },
  ];

  return (
    <BuyerPortalLayout title="Procurement Overview">
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2">
            <Link to="/account/marketplace" className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 transition">
              Browse Marketplace
            </Link>
            <Link to="/account/rfqs/new" className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition">
              Create RFQ
            </Link>
            <Link to="/account/orders" className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition">
              View Orders
            </Link>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <Link
                key={m.label}
                to={m.href}
                className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-md transition-shadow"
              >
                <p className={`text-2xl font-extrabold ${m.color.split(' ')[1]}`}>{m.value}</p>
                <p className="text-xs text-gray-500 mt-1">{m.label}</p>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Orders */}
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-700">Recent Orders</h3>
                <Link to="/account/orders" className="text-xs text-emerald-600 hover:text-emerald-700 font-bold">View all →</Link>
              </div>
              <div className="divide-y divide-gray-50">
                {recentOrders.length === 0 ? (
                  <div className="px-5 py-8 text-center">
                    <p className="text-sm text-gray-400">No orders yet</p>
                    <Link to="/account/marketplace" className="mt-2 text-xs text-emerald-600 font-bold hover:underline">Browse Marketplace →</Link>
                  </div>
                ) : (
                  recentOrders.map((o: any) => (
                    <Link key={o.id} to={`/account/orders/${o.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition">
                      <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                        <span className="text-sm">📦</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{o.order_number}</p>
                        <p className="text-xs text-gray-400">{o.item_count || 0} items · {new Date(o.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          o.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                          o.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {o.status?.replace(/_/g, ' ')}
                        </span>
                        <p className="text-xs font-mono text-gray-500 mt-0.5">{formatMoney(Number(o.total_amount) || 0, o || DEFAULT_CURRENCY)}</p>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>

            {/* Open RFQs */}
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-700">Open RFQs</h3>
                <Link to="/account/rfqs" className="text-xs text-emerald-600 hover:text-emerald-700 font-bold">View all →</Link>
              </div>
              <div className="divide-y divide-gray-50">
                {openRfqs.length === 0 ? (
                  <div className="px-5 py-8 text-center">
                    <p className="text-sm text-gray-400">No open RFQs</p>
                    <Link to="/account/rfqs/new" className="mt-2 text-xs text-emerald-600 font-bold hover:underline">Create RFQ →</Link>
                  </div>
                ) : (
                  openRfqs.map((rfq: any) => (
                    <Link key={rfq.id} to={`/account/rfqs/${rfq.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition">
                      <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
                        <span className="text-sm">📋</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{rfq.title}</p>
                        <p className="text-xs text-gray-400">{rfq.rfq_number}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
                          {rfq.status}
                        </span>
                        {rfq.quote_deadline && (
                          <p className="text-[10px] text-gray-400 mt-0.5">Due {new Date(rfq.quote_deadline).toLocaleDateString()}</p>
                        )}
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Supplier Directory */}
          <Link to="/account/suppliers" className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-md transition group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <span className="text-lg">🏪</span>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Verified Supplier Directory</p>
                <p className="text-xs text-gray-500">Browse verified suppliers and their products</p>
              </div>
            </div>
            <span className="text-sm font-bold text-emerald-600 group-hover:translate-x-1 transition-transform">→</span>
          </Link>
        </div>
      )}
    </BuyerPortalLayout>
  );
};

export default BuyerOverviewPage;
